import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { getVerticals, getLogos, createLogo, deleteLogo } from "@/lib/api";
import { Plus, Trash2, Loader2, Image } from "lucide-react";
import { toast } from "sonner";

export default function AdminLogos() {
  const [verticals, setVerticals] = useState([]);
  const [logos, setLogos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ vertical_id: "", name: "", logo_url: "" });
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try {
      const [verticalsRes, logosRes] = await Promise.all([
        getVerticals(),
        getLogos()
      ]);
      setVerticals(verticalsRes.data);
      setLogos(logosRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSave = async () => {
    if (!formData.vertical_id || !formData.name || !formData.logo_url) {
      toast.error("Please fill in all fields");
      return;
    }

    setSaving(true);
    try {
      await createLogo(formData);
      toast.success("Logo added successfully");
      setDialogOpen(false);
      setFormData({ vertical_id: "", name: "", logo_url: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to add logo");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this logo?")) return;
    try {
      await deleteLogo(id);
      toast.success("Logo deleted successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete logo");
    }
  };

  const getVerticalName = (id) => {
    const v = verticals.find(v => v.id === id);
    return v ? v.name : "Unknown";
  };

  return (
    <AdminLayout title="Client Logos">
      <div className="space-y-6" data-testid="admin-logos">
        <div className="flex items-center justify-between">
          <p className="text-slate-600">Manage client logos for each vertical</p>
          <Button 
            onClick={() => setDialogOpen(true)}
            className="bg-[#0066CC] hover:bg-[#0052A3]"
            data-testid="add-logo-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Logo
          </Button>
        </div>

        <Card className="border-slate-200">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-20">Preview</TableHead>
                  <TableHead>Client Name</TableHead>
                  <TableHead>Vertical</TableHead>
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" />
                    </TableCell>
                  </TableRow>
                ) : logos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                      No logos found. Click "Add Logo" to create one.
                    </TableCell>
                  </TableRow>
                ) : (
                  logos.map((logo) => (
                    <TableRow key={logo.id} className="data-table-row">
                      <TableCell>
                        <div className="w-16 h-12 rounded bg-slate-50 flex items-center justify-center overflow-hidden">
                          <img 
                            src={logo.logo_url} 
                            alt={logo.name}
                            className="max-w-full max-h-full object-contain"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              const fallback = document.createElement('span');
                              fallback.className = 'text-slate-400 text-xs';
                              fallback.textContent = 'No preview';
                              e.target.parentElement.appendChild(fallback);
                            }}
                          />
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{logo.name}</TableCell>
                      <TableCell>
                        <span className="px-2 py-1 rounded-full bg-[#E6F0FA] text-[#0066CC] text-xs">
                          {getVerticalName(logo.vertical_id)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(logo.id)}
                          className="text-slate-600 hover:text-red-500"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Add Client Logo</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Vertical *</Label>
                <Select 
                  value={formData.vertical_id} 
                  onValueChange={(v) => setFormData({ ...formData, vertical_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select vertical" />
                  </SelectTrigger>
                  <SelectContent>
                    {verticals.map((v) => (
                      <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Client Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Acme Corporation"
                  data-testid="logo-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Logo URL *</Label>
                <Input
                  value={formData.logo_url}
                  onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                  placeholder="https://..."
                  data-testid="logo-url-input"
                />
              </div>
              {formData.logo_url && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500 mb-2">Preview:</p>
                  <img 
                    src={formData.logo_url} 
                    alt="Preview" 
                    className="h-12 object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="bg-[#0066CC] hover:bg-[#0052A3]"
                data-testid="save-logo-btn"
              >
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Add Logo
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
